import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import s from "./style.module.scss";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import Card from "@material-ui/core/Card";
import { formatDateString } from "../../helpers/date-helpers";
import {
  getServiceMatchIsMatching,
  getServiceMatchIsLoading,
  getServiceMatchNeedsUpdate,
  getServiceMatchSearchText,
  getServiceMatchSelectedMatchedUser,
  getServiceMatchSelectedUser,
  getServiceMatchSuggestions,
  getServiceMatchUnmatchedData
} from "../../redux/root-reducer";
import {
  changeUserSearch,
  clearMatchedUser,
  clearSelectedUser,
  clearUserSuggestions,
  confirmServiceMatch,
  getUnmatchedUsers,
  selectUser,
  selectUserSuggestion
} from "../../redux/match-service";
import Grid from "@material-ui/core/Grid";
import AutoComplete from "../common/autocomplete";
import Button from "@material-ui/core/Button";
import CardList from "../common/card-list";
import CircularProgress from "@material-ui/core/CircularProgress";
import InfiniteScroll from "../common/infinite-scroll";

const pmrUserShape = PropTypes.shape({
  nhsNumber: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  dob: PropTypes.string.isRequired,
  addressLine1: PropTypes.string.isRequired,
  postcode: PropTypes.string.isRequired
});
const pharmacyUserShape = PropTypes.shape({
  userId: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  dob: PropTypes.string.isRequired,
  addressLine1: PropTypes.string.isRequired,
  postcode: PropTypes.string.isRequired
});

export const formatUserDescription = ({ dob, addressLine1, postcode }) =>
  `${formatDateString(dob)}, ${addressLine1}, ${postcode}`;

export function MatchServiceStepper({ activeStep }) {
  const steps = ["Select unknown user", "Find and match user"];
  return (
    <Stepper
      classes={{ root: s.userStepperRoot }}
      activeStep={activeStep}
      alternativeLabel
    >
      {steps.map(label => (
        <Step key={label}>
          <StepLabel>{label}</StepLabel>
        </Step>
      ))}
    </Stepper>
  );
}

MatchServiceStepper.propTypes = {
  activeStep: PropTypes.number
};

MatchServiceStepper.defaultProps = {
  activeStep: 1
};

export class MatchPanel extends React.Component {
  componentDidUpdate(prevProps) {
    const {
      selectedUser,
      userSearchText,
      selectedMatchUser,
      clearMatchedUser
    } = this.props;

    const prevSelectedUser = prevProps.selectedUser;
    if (prevSelectedUser != null && prevSelectedUser !== selectedUser) {
      clearMatchedUser();
    }

    if (userSearchText !== prevProps.userSearchText) {
      if (
        selectedMatchUser &&
        userSearchText !==
          `${selectedMatchUser.firstName} ${selectedMatchUser.lastName}`
      ) {
        clearMatchedUser();
      }
    }
  }

  render() {
    const {
      userSuggestions,
      selectedUser,
      selectedMatchUser,
      userSearchText,
      onUserSuggestionTextChange,
      onCloseUserSuggestions,
      onUserSuggestionSelect,
      onMatchClick,
      matching
    } = this.props;
    const suggestions = userSuggestions.map(u => ({
      name: `${u.firstName} ${u.lastName}`,
      description: formatUserDescription(u)
    }));

    const userDescription = selectedMatchUser ? (
      formatUserDescription(selectedMatchUser)
    ) : (
      <>&nbsp;</>
    );

    if (!selectedUser) {
      return null;
    }

    return (
      <div>
        <Card className={s.matchPanel}>
          <AutoComplete
            id={"m_autocomplete_user"}
            placeholder={"Search by user name"}
            text={userSearchText}
            inputVariant={"outlined"}
            onTextChange={t => onUserSuggestionTextChange(t)}
            onOuterClick={onCloseUserSuggestions}
            onSuggestionSelect={index =>
              onUserSuggestionSelect(userSuggestions[index])
            }
            suggestions={suggestions}
          />

          <p>{userDescription}</p>

          <Button
            fullWidth
            variant={"contained"}
            color={"primary"}
            className={s.matchButton}
            disabled={!selectedMatchUser || matching}
            onClick={() => onMatchClick("Direct-2-Patients")}
          >
            Match
          </Button>
          <Button
            fullWidth
            variant={"contained"}
            color={"primary"}
            className={s.matchButton}
            disabled={matching}
            onClick={() => onMatchClick("Care-Home")}
          >
            Assign to care homes
          </Button>
        </Card>
      </div>
    );
  }
}

MatchPanel.propTypes = {
  selectedUser: pmrUserShape,
  selectedMatchUser: pharmacyUserShape,
  userSuggestions: PropTypes.arrayOf(pharmacyUserShape).isRequired,
  userSearchText: PropTypes.string,
  onUserSuggestionTextChange: PropTypes.func.isRequired,
  onCloseUserSuggestions: PropTypes.func.isRequired,
  onUserSuggestionSelect: PropTypes.func.isRequired,
  clearMatchedUser: PropTypes.func.isRequired,
  onMatchClick: PropTypes.func.isRequired
};

MatchPanel.defaultProps = {
  selectedUser: undefined,
  selectedMatchUser: undefined,
  userSearchText: undefined
};

export class MatchService extends Component {
  renderUserCard = user => {
    return (
      <div className={s.userCardDetails}>
        <p className={"name"}>{`${user.firstName} ${user.lastName}`}</p>
        <p>{formatUserDescription(user)}</p>
      </div>
    );
  };

  componentDidMount() {
    this.props.getUnmatchedUsers();
  }

  componentDidUpdate(prevProps) {
    const { needsUpdate, getUnmatchedUsers } = this.props;

    if (!prevProps.needsUpdate && needsUpdate) {
      getUnmatchedUsers();
    }
  }

  componentWillUnmount() {
    this.props.clearSelectedUser();
  }

  render() {
    const {
      data,
      isLoading,
      selectedUser,
      selectUser,
      selectedMatchUser,
      userSuggestions,
      userSearchText,
      onUserSuggestionTextChange,
      onCloseUserSuggestions,
      onUserSuggestionSelect,
      clearMatchedUser,
      matching,
      onMatchClick,
      getUnmatchedUsers
    } = this.props;

    if (isLoading && data.page < 1) {
      return (
        <div className={s.root}>
          <div className={s.progress}>
            <CircularProgress id={"t_progress"} size={50} />
          </div>
        </div>
      );
    }

    if (data.users.length === 0) {
      return (
        <div id={"#m_noData"} className={s.noData}>
          <h2>There are currently no unmatched users.</h2>
        </div>
      );
    }

    const userCards = data.users.map(user => ({
      id: user.nhsNumber,
      data: user,
      selected: selectedUser && selectedUser.nhsNumber === user.nhsNumber
    }));

    return (
      <div className={s.pageWrapper}>
        <MatchServiceStepper activeStep={selectedUser ? 1 : 0} />

        <div>
          <Grid container spacing={32} classes={{ container: s.pageGrid }}>
            <Grid item xs={6}>
              <InfiniteScroll
                onReachThreshold={() => {
                  if (data.page < data.totalPages) {
                    getUnmatchedUsers(data.page + 1);
                  }
                }}
                isLoading={isLoading}
              >
                <CardList
                  selectorType={"radio"}
                  cards={userCards}
                  onCardSelectionChanged={selectUser}
                  render={this.renderUserCard}
                />
              </InfiniteScroll>
            </Grid>
            <Grid item xs={6}>
              <MatchPanel
                selectedUser={selectedUser}
                selectedMatchUser={selectedMatchUser}
                userSuggestions={userSuggestions}
                userSearchText={userSearchText}
                onUserSuggestionTextChange={onUserSuggestionTextChange}
                onCloseUserSuggestions={onCloseUserSuggestions}
                onUserSuggestionSelect={onUserSuggestionSelect}
                clearMatchedUser={clearMatchedUser}
                matching={matching}
                onMatchClick={onMatchClick}
              />
            </Grid>
          </Grid>
        </div>
      </div>
    );
  }
}

MatchService.propTypes = {
  selectedUser: pmrUserShape,
  selectedMatchUser: pharmacyUserShape,
  selectUser: PropTypes.func.isRequired,
  needsUpdate: PropTypes.bool,
  isLoading: PropTypes.bool,
  matching: PropTypes.bool,
  data: PropTypes.shape({
    users: PropTypes.arrayOf(pmrUserShape),
    page: PropTypes.number,
    totalPages: PropTypes.number
  }).isRequired,
  userSearchText: PropTypes.string,
  userSuggestions: PropTypes.arrayOf(pharmacyUserShape).isRequired,
  getUnmatchedUsers: PropTypes.func.isRequired,
  onUserSuggestionTextChange: PropTypes.func.isRequired,
  onCloseUserSuggestions: PropTypes.func.isRequired,
  onUserSuggestionSelect: PropTypes.func.isRequired,
  clearMatchedUser: PropTypes.func.isRequired,
  onMatchClick: PropTypes.func.isRequired,
  clearSelectedUser: PropTypes.func.isRequired
};

MatchService.defaultProps = {
  selectedUser: undefined,
  selectedMatchUser: undefined,
  userSearchText: undefined,
  needsUpdate: false,
  isLoading: false,
  matching: false,
  data: {
    users: [],
    page: 0,
    totalPages: 0
  }
};

export const mapDispatchToProps = {
  getUnmatchedUsers,
  selectUser,
  clearMatchedUser,
  onUserSuggestionTextChange: changeUserSearch,
  onCloseUserSuggestions: clearUserSuggestions,
  onUserSuggestionSelect: selectUserSuggestion,
  onMatchClick: confirmServiceMatch,
  clearSelectedUser
};

export const mapStateToProps = state => ({
  selectedUser: getServiceMatchSelectedUser(state),
  selectedMatchUser: getServiceMatchSelectedMatchedUser(state),
  userSuggestions: getServiceMatchSuggestions(state),
  userSearchText: getServiceMatchSearchText(state),
  needsUpdate: getServiceMatchNeedsUpdate(state),
  isLoading: getServiceMatchIsLoading(state),
  matching: getServiceMatchIsMatching(state),
  data: getServiceMatchUnmatchedData(state)
});

export const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps,
  onMatchClick: selectedService =>
    dispatchProps.onMatchClick(
      stateProps.selectedUser,
      stateProps.selectedMatchUser,
      selectedService
    )
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(MatchService);
